import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, CheckCircle, Trash, CircleNotch, ArrowRight,
  Info, Warning, XCircle, Link as LinkIcon,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { notificationsApi } from '../../api';

const POLL_INTERVAL_MS = 60_000;

const formatTime = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
};

const iconFor = (type) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('error') || t.includes('reject')) return XCircle;
  if (t.includes('warn') || t.includes('alert')) return Warning;
  if (t.includes('success') || t.includes('approve')) return CheckCircle;
  return Info;
};

const colorFor = (type) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('error') || t.includes('reject')) return 'var(--color-danger)';
  if (t.includes('warn') || t.includes('alert')) return 'var(--color-warning)';
  if (t.includes('success') || t.includes('approve')) return 'var(--color-success)';
  return 'var(--accent-primary)';
};

export function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await notificationsApi.list({ limit: 30 });
      if (res?.success) {
        setItems(res.data?.items || []);
        setUnread(res.data?.unread || 0);
      }
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificationsApi.unreadCount();
      if (res?.success) setUnread(res.data?.count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchList();

    // Try SSE first; fall back to polling on error.
    const token = localStorage.getItem('access_token');
    let es = null;
    let pollId = null;
    try {
      es = new EventSource(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/stream?t=${encodeURIComponent(token || '')}`
      );
      es.addEventListener('hello', (e) => {
        try {
          const d = JSON.parse(e.data);
          if (typeof d.unread === 'number') setUnread(d.unread);
        } catch { /* silent */ }
      });
      es.addEventListener('notification', () => {
        // New notification pushed — refresh list & count
        fetchList();
      });
      es.onerror = () => {
        if (es) {
          es.close();
          es = null;
        }
        // Fall back to polling
        if (!pollId) pollId = setInterval(fetchCount, POLL_INTERVAL_MS);
      };
    } catch {
      pollId = setInterval(fetchCount, POLL_INTERVAL_MS);
    }

    // Also poll at a slower cadence as a safety net
    const safetyPoll = setInterval(fetchCount, POLL_INTERVAL_MS);

    return () => {
      if (es) es.close();
      if (pollId) clearInterval(pollId);
      clearInterval(safetyPoll);
    };
  }, [fetchList, fetchCount]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) fetchList();
  };

  const onItemClick = async (n) => {
    try {
      if (!n.is_read) {
        await notificationsApi.markRead(n.id);
      }
    } catch { /* silent */ }
    setOpen(false);
    if (n.action_url) {
      try {
        const u = new URL(n.action_url, window.location.origin);
        if (u.origin === window.location.origin) {
          navigate(u.pathname + u.search + u.hash);
          return;
        }
        window.open(n.action_url, '_blank');
      } catch {
        navigate(n.action_url);
      }
    }
  };

  const markAll = async () => {
    try {
      await notificationsApi.markAllRead();
      fetchList();
    } catch { /* silent */ }
  };

  const dismiss = async (e, id) => {
    e.stopPropagation();
    try {
      await notificationsApi.dismiss(id);
      fetchList();
    } catch { /* silent */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full relative"
          style={{ color: 'var(--text-secondary)' }}
          title="Notifications"
          data-testid="notifications-bell"
        >
          <Bell className="h-[18px] w-[18px]" weight="duotone" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center text-[9px] font-bold"
              style={{
                background: 'var(--accent-primary)',
                color: '#0A0E14',
                boxShadow: '0 0 0 2px var(--bg-header), 0 0 8px var(--accent-primary-ring)',
                fontFamily: 'var(--font-heading)',
              }}
              data-testid="notifications-unread-count"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="w-[400px] p-0 overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-default)',
          boxShadow: 'var(--shadow-elevated)',
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" weight="duotone" style={{ color: 'var(--accent-primary)' }} />
            <span
              className="text-[14px]"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--text-primary)' }}
            >
              Notifications
            </span>
            {unread > 0 && (
              <span
                className="px-1.5 py-0.5 text-[10px] rounded-full"
                style={{ background: 'var(--accent-primary-muted)', color: 'var(--accent-primary)', fontFamily: 'var(--font-heading)', fontWeight: 600 }}
              >
                {unread} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={markAll}
              disabled={!unread}
              className="text-[11px] uppercase tracking-[0.12em] hover:underline disabled:opacity-40"
              style={{ color: 'var(--accent-primary)', fontFamily: 'var(--font-heading)', fontWeight: 600 }}
              data-testid="notifications-mark-all-read"
            >
              Mark all read
            </button>
          </div>
        </div>

        <div className="max-h-[440px] overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-10 gap-2 text-[13px]"
              style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
              Loading...
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-[13px]"
              style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
              No notifications yet
            </div>
          ) : (
            <ul>
              {items.map((n) => {
                const Icon = iconFor(n.type);
                const color = colorFor(n.type);
                const isUnread = !n.is_read;
                return (
                  <li
                    key={n.id}
                    className="flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors group"
                    style={{
                      background: isUnread ? 'var(--accent-primary-soft)' : 'transparent',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isUnread ? 'var(--accent-primary-soft)' : 'transparent'; }}
                    onClick={() => onItemClick(n)}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div
                      className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--bg-secondary)', border: `1px solid ${color}33` }}
                    >
                      <Icon className="h-4 w-4" weight="duotone" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[12.5px] leading-snug"
                        style={{ fontFamily: 'var(--font-body)', color: 'var(--text-primary)', fontWeight: 600 }}
                      >
                        {n.title || n.event_key || 'Notification'}
                      </p>
                      {n.body && (
                        <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {n.body}
                        </p>
                      )}
                      <p
                        className="text-[11px] mt-0.5 flex items-center gap-1.5"
                        style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}
                      >
                        {n.actor_name && <span>{n.actor_name}</span>}
                        <span>· {formatTime(n.created_at)}</span>
                        {n.action_url && <LinkIcon className="h-3 w-3" weight="bold" />}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => dismiss(e, n.id)}
                        className="p-1 rounded"
                        style={{ color: 'var(--color-danger)' }}
                        title="Dismiss"
                        data-testid={`notification-dismiss-${n.id}`}
                      >
                        <Trash className="h-3.5 w-3.5" weight="bold" />
                      </button>
                    </div>
                    {isUnread && (
                      <span
                        className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                        style={{ background: 'var(--accent-primary)', boxShadow: '0 0 6px var(--accent-primary-ring)' }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <button
          onClick={() => { setOpen(false); navigate('/audit-logs'); }}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[12px] transition-colors"
          style={{
            background: 'var(--bg-hover)',
            borderTop: '1px solid var(--border-default)',
            color: 'var(--accent-primary)',
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
          data-testid="notifications-view-audit"
        >
          View audit logs
          <ArrowRight className="h-3.5 w-3.5" weight="bold" />
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationsBell;
